import React from 'react';
import { Plus, CheckCircle2, Check } from 'lucide-react';
import './VoucherRulesTab.css';

// ================== DATA (استبدلها بالداتا من الـ API) ==================
const summaryStats = [
  { label: 'Eligible Users', value: '3,200' },
  { label: 'Excluded Users', value: '420' },
  { label: 'Pending Check', value: '85' },
  { label: 'Total Evaluated', value: '3,705' },
];

const includeRules = [
  { id: 1, rule: 'Customer Segment', condition: 'is', value: 'VIP Customers', match: 'AND', status: 'Active' },
  { id: 2, rule: 'Loyalty Tier', condition: 'is one of', value: 'Gold, Platinum, Diamond', match: 'AND', status: 'Active' },
  { id: 3, rule: 'Total Orders Count', condition: 'greater than or equal', value: '10', match: 'AND', status: 'Active' },
  { id: 4, rule: 'Total Spent (EGP)', condition: 'greater than or equal', value: '1000', match: 'AND', status: 'Active' },
];

const excludeRules = [
  { id: 1, rule: 'Existing Voucher Users', condition: 'is', value: 'Yes', match: 'OR', status: 'Active' },
  { id: 2, rule: 'Account Status', condition: 'is', value: 'Suspended', match: 'OR', status: 'Active' },
  { id: 3, rule: 'Employee Accounts', condition: 'is', value: 'Internal Employee', match: 'OR', status: 'Active' },
  { id: 4, rule: 'Test / Dummy Accounts', condition: 'is', value: 'Yes', match: 'OR', status: 'Active' },
];

const voucherRules = [
  { label: 'Discount Type', value: 'Fixed Amount' },
  { label: 'Discount Value', value: '150 EGP' },
  { label: 'Minimum Order Value', value: '0 EGP' },
  { label: 'Maximum Discount', value: '150 EGP' },
  { label: 'Usage Limit', value: '1 time per customer' },
  { label: 'Stackable', value: 'No' },
  { label: 'Applicable Categories', value: 'All Categories' },
  { label: 'Payment Method', value: 'All Methods' },
  { label: 'Validity', value: 'Mar 10 - May 31, 2025' },
];

const validationChecklist = [
  'Voucher is active',
  'Budget available and sufficient',
  'Rules configured correctly',
  'Usage limit is valid',
  'Target audience size is valid',
];

// ================== SUB COMPONENTS ==================
function StepBadge({ n }) {
  return <span className="vru-step-badge">{n}</span>;
}

function RulesTable({ rows }) {
  return (
    <div className="vru-table-wrap">
      <table className="vru-table">
        <thead>
          <tr>
            <th className="vru-col-num">#</th>
            <th>Rule</th>
            <th>Condition</th>
            <th></th>
            <th>Match</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="vru-col-num">{r.id}</td>
              <td>{r.rule}</td>
              <td>{r.condition}</td>
              <td>{r.value}</td>
              <td>{r.match}</td>
              <td>
                <span className="vru-status-pill">{r.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ================== MAIN COMPONENT ==================
export default function VoucherRulesTab() {
  return (
    <div className="vru-grid">
      {/* ---------- MAIN COLUMN ---------- */}
      <div className="vru-main">
        <div className="vru-panel">
          {/* 1. Eligibility Summary */}
          <div className="vru-panel-section">
            <div className="vru-card-header">
              <StepBadge n={1} />
              <h3 className="vru-card-title">Eligibility Summary</h3>
            </div>
            <p className="vru-desc">This voucher is available to users who meet all the eligibility conditions below.</p>

            <div className="vru-summary-grid">
              {summaryStats.map((s) => (
                <div className="vru-summary-item" key={s.label}>
                  <span className="vru-summary-label">{s.label}</span>
                  <span className="vru-summary-value">{s.value}</span>
                </div>
              ))}
            </div>

            <div className="vru-status-block">
              <span className="vru-summary-label">Eligibility Status</span>
              <span className="vru-eligibility-pill">Active</span>
            </div>
          </div>
          <div className="vru-panel-divider" />
          {/* 2. Include Users */}
          <div className="vru-panel-section">
            <div className="vru-card-header-row">
              <div className="vru-card-header">
                <StepBadge n={2} />
                <h3 className="vru-card-title">Include Users</h3>
              </div>
              <button className="vru-add-btn vru-add-btn-include">
                <Plus size={14} /> Add Condition
              </button>
            </div>
            <p className="vru-desc">Define who is eligible to use this voucher</p>

            <RulesTable rows={includeRules} />
          </div>
          <div className="vru-panel-divider" />
          {/* 3. Exclude Users */}
          <div className="vru-panel-section">
            <div className="vru-card-header-row">
              <div className="vru-card-header">
                <StepBadge n={3} />
                <h3 className="vru-card-title">Exclude Users</h3>
              </div>
              <button className="vru-add-btn vru-add-btn-exclude">
                <Plus size={14} /> Add Condition
              </button>
            </div>
            <p className="vru-desc">Define who is not eligible to use this voucher</p>

            <RulesTable rows={excludeRules} />
          </div>
        </div>
      </div>

      {/* ---------- SIDEBAR ---------- */}
      <div className="vru-sidebar">
        {/* Rule Health */}
        <div className="vru-card">
          <h3 className="vru-card-title">Rule Health</h3>
          <div className="vru-health-row">
            <span className="vru-health-icon">
              <CheckCircle2 size={18} color="#fff" />
            </span>
            <span className="vru-health-title">Healthy</span>
          </div>
          <p className="vru-health-line">All rules are valid</p>
          <p className="vru-health-sub">No conflicts detected</p>
        </div>

        {/* Vouchers Rules */}
        <div className="vru-card">
          <h3 className="vru-card-title">Vouchers Rules</h3>
          <div className="vru-rules-list">
            {voucherRules.map((r, i) => (
              <div className="vru-rule-item" key={r.label}>
                <span className="vru-rule-num">{i + 1}</span>
                <span className="vru-rule-label">{r.label}</span>
                <span className="vru-rule-value">{r.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Validation Checklist */}
        <div className="vru-card">
          <h3 className="vru-card-title">Validation Checklist</h3>
          <div className="vru-checklist">
            {validationChecklist.map((item) => (
              <div className="vru-check-item" key={item}>
                <span className="vru-check-icon">
                  <Check size={12} color="#fff" />
                </span>
                <span className="vru-check-label">{item}</span>
                <span className="vru-pass-pill">Pass</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}